<script setup lang="ts">
  import { FileType } from '@api/models';
  import type { ZoomLevel } from '@vue-pdf-viewer/viewer';
  import { defineAsyncComponent } from 'vue';

  import CsvViewer from '@ui/components/csv-viewer/CsvViewer.vue';
  import HtmlViewer from '@ui/components/html-viewer/HtmlViewer.vue';
  import PlaintextViewer from '@ui/components/plaintext-viewer/PlaintextViewer.vue';
  import XlsxViewer from '@ui/components/xlsx-viewer/XlsxViewer.vue';
  import { downloadFile } from '@ui/utils/utils';

  interface PreviewableAttachment {
    file_type?: string | null;
    mime_type?: string | null;
    name: string;
    src?: string | null;
  }

  const props = defineProps<{ attachment: PreviewableAttachment }>();

  const PDF_INITIAL_SCALE = 'pageWidth' as ZoomLevel;

  const PDFViewer = defineAsyncComponent(async () => {
    const { setLicense } = await import('@ui/components/pdf-viewer/index');
    setLicense(process.env.PDF_VIEWER_LICENSE_KEY || '');
    return import('@ui/components/pdf-viewer/PDFViewer.vue');
  });

  const downloadAttachment = () => {
    if (props.attachment.src) {
      downloadFile(props.attachment.src, props.attachment.name);
    }
  };
</script>

<template>
  <template v-if="!attachment.src">
    <span class="text-destructive"> Something went wrong while trying to preview. </span>
  </template>
  <template v-else>
    <PDFViewer
      v-if="attachment.mime_type === 'application/pdf'"
      :src="attachment.src"
      :download-filename="attachment.name"
      :initial-scale="PDF_INITIAL_SCALE"
      :text-layer="true"
      :toolbar-options="{
        pointerSwitchable: true,
        docPropertiesEnabled: false,
        jumpNavigatable: false,
        rotatable: true,
        themeSwitchable: false,
        sidebarEnable: false,
        searchable: false,
        navigatable: true,
        downloadable: true,
        zoomable: true,
        printable: true,
        newFileOpenable: false,
        fullscreen: true,
        pageViewSwitchable: false,
        scrollingSwitchable: false,
      }"
      class="h-[70vh] w-full"
    />

    <div
      v-else-if="attachment.file_type === FileType.IMAGE || attachment.file_type === FileType.GIF"
      class="h-full overflow-auto"
    >
      <img :src="attachment.src" class="mx-auto h-auto w-full max-w-full object-contain" />
    </div>

    <audio v-else-if="attachment.file_type === FileType.AUDIO" controls class="w-full">
      <source :src="attachment.src" :type="attachment.mime_type" />
    </audio>

    <CsvViewer
      v-else-if="attachment.mime_type === 'text/csv'"
      :url="attachment.src"
      :file-name="attachment.name"
      :mime-type="attachment.mime_type"
    >
      <template #csv_viewer_actions>
        <slot name="csv_document_title" />
      </template>
    </CsvViewer>

    <XlsxViewer
      v-else-if="
        attachment.mime_type === 'application/vnd.ms-excel' ||
        attachment.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      "
      :url="attachment.src"
      :file-name="attachment.name"
      :mime-type="attachment.mime_type"
    >
      <template #xlsx_viewer_actions>
        <slot name="xlsx_document_title" />
      </template>
    </XlsxViewer>

    <PlaintextViewer
      v-else-if="attachment.mime_type === 'text/plain'"
      :url="attachment.src"
      :file-name="attachment.name"
      :mime-type="attachment.mime_type"
    >
      <template #plaintext_viewer_actions>
        <slot name="plaintext_document_title" />
      </template>
    </PlaintextViewer>

    <HtmlViewer
      v-else-if="attachment.mime_type === 'text/html'"
      :url="attachment.src"
      :file-name="attachment.name"
      :mime-type="attachment.mime_type"
    >
      <template #html_viewer_actions>
        <slot name="html_document_title" />
      </template>
    </HtmlViewer>

    <span v-else class="font-medium">
      Unable to preview this file.
      <span
        class="cursor-pointer font-medium text-blue hover:text-blue/50"
        @click="downloadAttachment"
      >
        Download {{ attachment.name }}
      </span>
    </span>
  </template>
</template>
